import React from "react";
import { format, isValid, parseISO } from "date-fns";
import { Link, Tooltip } from "@merge-api/merge-javascript-shared";
import { DeprecationStage } from "../../../../../../constants";
import { DeprecationInfo } from "./deprecation";

// parseISO reads a date-only "YYYY-MM-DD" as local midnight (new Date("YYYY-MM-DD") parses as UTC
// and can shift the day across time zones). Returns null when the string isn't a valid date.
const parseDeprecationDate = (iso: string): Date | null => {
  const date = parseISO(iso);
  return isValid(date) ? date : null;
};

/**
 * True when the item is already deprecated, so its text columns dim to 60% (the switch keeps full
 * opacity and stays toggleable). A past-dated deprecation counts; a future ("will be deprecated")
 * date does not. With no date, a DEPRECATED-stage item is already deprecated now, so it dims too
 * (e.g. Employee.hire_date, which carries only a stage) — SUNSET / message-only items are not dimmed.
 */
export const isPastDeprecation = (info: DeprecationInfo): boolean => {
  if (!info.deprecationDate) return info.deprecationStage === DeprecationStage.DEPRECATED;
  const date = parseDeprecationDate(info.deprecationDate);
  return !!date && date.getTime() < Date.now();
};

/**
 * Resolves the deprecation message shown beneath a field/model row. A hand-authored
 * `deprecationMessage` wins; otherwise, when a `deprecationDate` is set, the copy is generated from
 * it: "Will be deprecated {date}. Use {X} instead" for a future date, or "Deprecated on {date}.
 * Use {X} instead" for a past date. The replacement reference (when present) is a gray-50
 * underlined link that scrolls to its anchor row on click.
 */
export const resolveDeprecationMessage = (info: DeprecationInfo): React.ReactNode => {
  if (info.deprecationMessage) return info.deprecationMessage;
  // Stage-only deprecation (no hand-authored message, no date): fall back to a plain "Deprecated"
  // label so the field still gets a visible cue (e.g. Employee.hire_date). Items with no stage at
  // all have no deprecation and get no message.
  if (!info.deprecationDate) return info.deprecationStage ? "Deprecated" : undefined;

  const date = parseDeprecationDate(info.deprecationDate);
  if (!date) return undefined;
  const isPast = date.getTime() < Date.now();
  const formattedDate = format(date, "MMMM do, yyyy"); // e.g. "June 30th, 2026"

  const replacement = info.deprecationReplacement;
  // Match the relation-note link exactly: gray-50 (#565551), monospace, underlined, a "Go to X"
  // tooltip, and a smooth-scroll to the replacement model's row on click. Don't pass color via
  // Link's normal path — passing className clobbers Link's built-in blue.
  const replacementLink = replacement ? (
    <Tooltip title={`Go to ${replacement.label}`}>
      <Link
        variant="sm-mono"
        as="a"
        onClick={() =>
          replacement.anchor &&
          document
            .getElementById(replacement.anchor)
            ?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
        className="cursor-pointer text-gray-50 underline"
      >
        {replacement.label}
      </Link>
    </Tooltip>
  ) : null;

  if (isPast) {
    return (
      <>
        Deprecated on {formattedDate}.{replacement && <> Use {replacementLink} instead.</>}
      </>
    );
  }
  return (
    <>
      Will be deprecated {formattedDate}.{replacement && <> Use {replacementLink} instead.</>}
    </>
  );
};
